import { RichText, Text } from '@sitecore-jss/sitecore-jss-react';
import clsx from 'clsx';
import React, { useState, useEffect } from 'react';
import Sticky from 'react-sticky-el';
import { useInView } from 'react-intersection-observer';
import '../CarLoanCalculator/style.scss';
import './style.scss';
import { withTranslation } from 'react-i18next';
import SingleInput from '../CarLoanCalculator/SingleInput';
import { PMT, InputField, isNumber } from '../CarLoanCalculator/helpers';
import ScheduleModal from '../CarLoanCalculator/ScheduleModal';

const MortgageCalculator = ({ fields = {}, t, params }) => {
  // main
  const { title, description, currency, exposure, maxDownpaymentOffset } = fields;
  // property price
  const {
    propertyPriceLabel,
    propertyPriceSubLabel,
    propertyPriceDefault,
    propertyPriceMin,
    propertyPriceMax,
    propertyPriceMaxLength,
    propertyPricePlaceholder,
    propertyPriceStep,
    propertyPriceAdornmentText,
  } = fields;
  // down payment
  /**
   * TODO: get minimum % from backend
   */
  const {
    borrowLabel,
    borrowSubLabel,
    borrowDefault,
    borrowMax,
    borrowMaxLength,
    borrowPlaceholder,
    borrowStep,
    borrowAdornmentText,
  } = fields;
  // mortgage period
  const {
    payBackLabel,
    payBackSubLabel,
    tenureDefault,
    tenureMax,
    tenureMaxLength,
    tenureMin,
    tenurePlaceholder,
    tenureStep,
    tenureAdornmentText,
  } = fields;
  // interest
  const {
    interestRateDefault,
    interestRateLabel,
    interestRateMax,
    interestRateMaxLength,
    interestRateMin,
    interestRatePlaceholder,
    interestRateSubLabel,
    interestRateStep,
    interestRateAdornmentText,
  } = fields;

  // right-card
  const {
    monthlyRepaymentLabel,
    totalRepayableLabel,
    indicativeAmortisationScheduledButton,
    additionalNote,
  } = fields;
  // table-fields
  const {
    amortisationScheduleLabel,
    amortisationScheduleText,
    paymentNoLabel,
    installmentAmountLabel,
    principalLabel,
    interestLabel,
    closingBalanceLabel,
    openingBalanceLabel,
    amortisationScheduleAdditionalText,
  } = fields;

  const tableFields = {
    amortisationScheduleLabel,
    amortisationScheduleText,
    paymentNoLabel,
    installmentAmountLabel,
    principalLabel,
    interestLabel,
    closingBalanceLabel,
    openingBalanceLabel,
    amortisationScheduleAdditionalText,
  };

  //main
  const shouldRenderTitle = !!title?.value;
  const shouldRenderDescription = !!description?.value;
  const shouldRenderCurrency = !!currency?.value;

  // right-card
  const shouldRenderMonthlyRepaymentLabel = !!monthlyRepaymentLabel?.value;
  const shouldRenderTotalRepayableLabel = !!totalRepayableLabel?.value;
  const shouldRenderIndicativeAmortisationScheduledButton =
    !!indicativeAmortisationScheduledButton?.value;
  const shouldRenderAdditionalNote = !!additionalNote?.value;

  // inputs state
  const [propertyPriceValue, setpropertyPriceValue] = useState(propertyPriceDefault?.value);
  const [borrowValue, setborrowValue] = useState(borrowDefault?.value);
  const [mortgagePeriodValue, setMortgagePeriodValue] = useState(tenureDefault?.value);
  const [interestValue, setInterestValue] = useState(interestRateDefault?.value);

  // results state
  const [mortgageAmount, setMortgageAmount] = useState(0);
  const [monthlyRepayment, setMonthlyRepayment] = useState(0);

  useEffect(() => {
    if (!isNumber(propertyPriceValue)) {
      return;
    }
    if (propertyPriceValue * (exposure?.value || 0.65) < (borrowMax?.value || 20000000)) {
      setborrowValue(propertyPriceValue * (1 - (exposure?.value || 0.65)));
    } else {
      setborrowValue(propertyPriceValue - (borrowMax?.value || 20000000));
    }
  }, [propertyPriceValue]);

  useEffect(() => {
    if (
      !isNumber(propertyPriceValue) ||
      !isNumber(borrowValue) ||
      !isNumber(mortgagePeriodValue) ||
      !isNumber(interestValue)
    ) {
      return;
    }
    const loanAmount = propertyPriceValue - borrowValue;
    setMortgageAmount(loanAmount);
    const res = -PMT(interestValue / 100 / 12, mortgagePeriodValue * 12, loanAmount);
    setMonthlyRepayment(res);
  }, [propertyPriceValue, borrowValue, mortgagePeriodValue, interestValue]);

  // property price props
  const propertyPriceProps = new InputField(
    propertyPriceLabel,
    propertyPriceSubLabel,
    propertyPriceDefault,
    propertyPriceMin,
    propertyPriceMax,
    propertyPriceMaxLength,
    propertyPricePlaceholder,
    propertyPriceStep,
    propertyPriceAdornmentText
  );
  // down payment props
  let calculatedBorrow;
  if (propertyPriceValue * (exposure?.value || 0.65) < (borrowMax?.value || 20000000)) {
    calculatedBorrow = propertyPriceValue * (1 - (exposure?.value || 0.65));
  } else {
    calculatedBorrow = propertyPriceValue - (borrowMax?.value || 20000000);
  }
  const borrowProps = new InputField(
    borrowLabel,
    borrowSubLabel,
    { value: calculatedBorrow },
    { value: calculatedBorrow },
    { value: propertyPriceValue - (maxDownpaymentOffset?.value || 15000) },
    borrowMaxLength,
    borrowPlaceholder,
    borrowStep,
    borrowAdornmentText
  );
  // morgage period props
  const mortgagePeriodProps = new InputField(
    payBackLabel,
    payBackSubLabel,
    tenureDefault,
    tenureMin,
    tenureMax,
    tenureMaxLength,
    tenurePlaceholder,
    tenureStep,
    tenureAdornmentText
  );
  // interest props
  const interestProps = new InputField(
    interestRateLabel,
    interestRateSubLabel,
    interestRateDefault,
    interestRateMin,
    interestRateMax,
    interestRateMaxLength,
    interestRatePlaceholder,
    interestRateStep,
    interestRateAdornmentText
  );

  const [componentRef, inView] = useInView({
    /* Optional options */
    threshold: 0.5,
    triggerOnce: false,
  });

  // Schedule popup
  const [showModal, setShowModal] = useState(false);

  return (
    <div
      ref={componentRef}
      className={clsx('mortgage-calculator content', 'add-margins', params?.Styles)}
    >
      <ScheduleModal
        showModal={showModal}
        setShowModal={setShowModal}
        payBackValue={mortgagePeriodValue * 12}
        borrow={mortgageAmount}
        monthlyRepayment={monthlyRepayment}
        data={{ ...tableFields }}
        reducingRate={interestValue}
        personal
      />
      {(shouldRenderTitle || shouldRenderDescription) && (
        <div className="car-loan-heading">
          {shouldRenderTitle && (
            <h3>
              <Text field={title} />
            </h3>
          )}
          {shouldRenderDescription && (
            <RichText className="car-loan-heading_description" field={description} />
          )}
        </div>
      )}
      <div className="car-loan-calculator-content-container">
        <div className="content-container-left">
          <SingleInput
            t={t}
            {...propertyPriceProps}
            value={propertyPriceValue}
            setValue={setpropertyPriceValue}
          />
          <SingleInput t={t} {...borrowProps} value={borrowValue} setValue={setborrowValue} />
          <SingleInput
            t={t}
            {...mortgagePeriodProps}
            value={mortgagePeriodValue}
            numbersOnly
            setValue={setMortgagePeriodValue}
          />
          <SingleInput
            t={t}
            {...interestProps}
            value={interestValue}
            withDecimal
            setValue={setInterestValue}
          />
        </div>
        <div className={clsx('car-loan-mobile d-xl-none', inView && 'toggled')}>
          <div className="content-container-right">
            <div className="result-card">
              {shouldRenderMonthlyRepaymentLabel && (
                <>
                  <div className="monthly__repayment--label result-label">
                    <Text field={monthlyRepaymentLabel} />
                  </div>
                  <div className="monthly__repayment--value result-value">
                    {shouldRenderCurrency && (
                      <span>
                        <Text field={currency} />
                      </span>
                    )}
                    <div>
                      {typeof mortgageAmount === 'number' &&
                        Math?.round(mortgageAmount)?.toLocaleString()}
                    </div>
                  </div>
                </>
              )}
              {shouldRenderTotalRepayableLabel && (
                <>
                  <div className="total__repayable--label result-label">
                    <Text field={totalRepayableLabel} />
                  </div>
                  <div className="total__repayable--value result-value">
                    {shouldRenderCurrency && (
                      <span>
                        <Text field={currency} />
                      </span>
                    )}
                    <div>
                      {typeof monthlyRepayment === 'number' &&
                        Math?.round(monthlyRepayment)?.toLocaleString()}
                    </div>
                  </div>
                </>
              )}
              {shouldRenderAdditionalNote && <RichText className="note" field={additionalNote} />}
              {shouldRenderIndicativeAmortisationScheduledButton && (
                <button
                  onClick={() => setShowModal(true)}
                  className="button button-contained-secondary btn-schedule"
                >
                  {indicativeAmortisationScheduledButton?.value}
                </button>
              )}
            </div>
          </div>
        </div>
        <Sticky
          className="car-loan-sticky d-none d-xl-block"
          bottomOffset={0}
          boundaryElement=".car-loan-calculator-content-container"
        >
          <div className="content-container-right">
            <div className="result-card">
              {shouldRenderMonthlyRepaymentLabel && (
                <>
                  <div className="monthly__repayment--label result-label">
                    <Text field={monthlyRepaymentLabel} />
                  </div>
                  <div className="monthly__repayment--value result-value">
                    {shouldRenderCurrency && (
                      <span>
                        <Text field={currency} />
                      </span>
                    )}
                    <div>
                      {typeof mortgageAmount === 'number' &&
                        Math?.round(mortgageAmount)?.toLocaleString()}
                    </div>
                  </div>
                </>
              )}
              {shouldRenderTotalRepayableLabel && (
                <>
                  <div className="total__repayable--label result-label">
                    <Text field={totalRepayableLabel} />
                  </div>
                  <div className="total__repayable--value result-value">
                    {shouldRenderCurrency && (
                      <span>
                        <Text field={currency} />
                      </span>
                    )}
                    <div>
                      {typeof monthlyRepayment === 'number' &&
                        Math?.round(monthlyRepayment)?.toLocaleString()}
                    </div>
                  </div>
                </>
              )}
              {shouldRenderAdditionalNote && <RichText className="note" field={additionalNote} />}
              {shouldRenderIndicativeAmortisationScheduledButton && (
                <button
                  onClick={() => setShowModal(true)}
                  className="button button-contained-secondary btn-schedule"
                >
                  {indicativeAmortisationScheduledButton?.value}
                </button>
              )}
            </div>
          </div>
        </Sticky>
      </div>
    </div>
  );
};

export default withTranslation()(MortgageCalculator);
